import React, { useEffect, useState } from 'react'
import {
  CAlert,
  CButton,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../../shared/icons'
import { useBostonState, emit } from '../lib/useBoston'
import { state, Storage, uuidV4 } from '../lib/store'
import { FIELD_TYPES, REMARK_TYPES, RECIPES } from '../lib/catalogs'
import {
  csReindexTags,
  csLoadRuntimeTemplates,
  csBuildDataJson,
  csBuildConfigJson,
  csPersistAndPublish,
  csProposeToKB,
  csBuildZip,
  csBuildSingleFile,
  csBuildPublishPayload,
  csCmdLabel,
  csFieldNoWrite,
} from '../lib/builder'
import {
  biaPublishReleaseCustomScript,
  biaPublishReleaseTestCustomScript,
  biaPublishTestCustomScript,
  biaSaveDraftCustomScript,
  biaValidateCustomScriptPublish,
} from '../lib/biaApi'
import { useUI } from '../../../shared/ui/UIProvider'

/* Port fiel de viewCustom/bindCustom de boston_ia (+ showPreviewModal):
   Custom Script Builder — genera el paquete de 6 archivos (modelo Basset / Sabre
   Custom Scripts). Incluye el catalogo de RECIPES, el preview live del form,
   el modal de vista previa funcional (single-file en iframe) y las exportaciones. */

/* ===== newField / mkField (port verbatim) =====
   Las RECIPES de catalogs.js llaman mkField() dentro de sus closures `apply`.
   En el original era una funcion global del index.html; aca la exponemos en
   window para que esos closures la resuelvan al ejecutarse. */
function newField() {
  return {
    label: 'Nuevo campo',
    typedata: 'text',
    typeremark: 'General',
    remark_mask: '',
    block: 0,
    mandatory: false,
    datasource: [],
    tag: '',
  }
}

function mkField(o) {
  return Object.assign(newField(), o)
}

if (typeof window !== 'undefined') {
  window.mkField = mkField
  window.newField = newField
}

/* ===== csApplyRecipe (port verbatim, render() -> emit()) ===== */
function csApplyRecipe(id) {
  const r = RECIPES.find((x) => x.id === id)
  if (!r) return
  const preset = r.apply()
  Object.assign(state.cs, preset)
  state.cs.uuid = uuidV4()
  state.cs.persisted = false
  state.cs.isSaved = false
  state.cs.url = ''
  state.cs.config = {}
  if (!state.cs.postCommands) state.cs.postCommands = []
  csReindexTags()
  emit()
}

/* ===== csPreviewRecipe (port): arma dataJson/configJson de la receta SIN tocar
   el Custom Script actual del builder; el caller abre el modal con el resultado. */
function csBuildRecipePreview(id) {
  const r = RECIPES.find((x) => x.id === id)
  if (!r) return null
  const preset = r.apply()
  // Reindexar tags del preset sin mutar state.cs
  let n = 0
  for (const form of preset.forms || []) {
    for (const field of form.remarks || []) {
      field.tag = 'field_' + n
      n++
    }
  }
  const dataJson = {
    title: preset.title,
    scriptType: 'custom',
    editCatalogues: false,
    reintentos: 0,
    sendCommands: !!preset.sendCommands,
    orderExecute: true,
    validateActiveReservation: !!preset.validateActiveReservation,
    forms: preset.forms || [],
    postCommands: preset.postCommands || [],
    targetEnv: state.cs.targetEnv || 'stg',
  }
  const configJson = { config: { uuid: 'preview-recipe-' + Date.now(), useInternalConfig: true } }
  return { dataJson, configJson }
}

/* ===== Descarga generica de un Blob (mismo mecanismo del original) ===== */
function downloadBlob(blob, filename) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
}

/* ===========================================================================
   PREVIEW UNIFICADO DE CAMPOS — port de renderScriptFieldsPreview: misma vista
   (grupos ocultos 🔒, "escribe:", "Ej:", opciones del select, postCommands). */
function ScriptFieldsPreview({ r }) {
  const forms = (r && r.forms) || []
  const post = (r && r.postCommands) || []
  return (
    <>
      <h4 style={{ margin: '14px 0 8px', color: 'var(--brand-dark)', fontSize: 14 }}>
        📋 Campos del formulario (vista del agente)
      </h4>
      <div style={{ fontSize: 11, color: 'var(--muted)', margin: '-4px 0 8px' }}>
        Las marcadas{' '}
        <span className="bia-pill mini" style={{ background: 'rgba(148,163,184,.25)' }}>
          🔒 auto - oculta
        </span>{' '}
        las arma el script solo (grupo oculto): el vendedor no las ve, pero igual se escriben.
      </div>
      {forms.length === 0 ? (
        <div className="bia-empty">Sin forms — solo postCommands.</div>
      ) : (
        forms.map((form, fi) => (
          <div key={fi} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {forms.length > 1 && (
                <span className="bia-pill mini">
                  Paso {fi + 1} de {forms.length}
                </span>
              )}
              <strong style={{ fontSize: 14 }}>{form.title || form.form || `Form ${fi + 1}`}</strong>
            </div>
            {(form.remarks || []).length === 0 ? (
              <div className="bia-empty" style={{ padding: 8, fontSize: 12 }}>
                Sin campos en este paso.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.remarks.map((field, idx) => {
                  const hidden = /oculto/i.test(field.group || '')
                  const code =
                    field.block != null && field.block !== '' && !isNaN(field.block) && Number(field.block) < 90
                      ? String(field.block)
                      : ''
                  const val = field.dato != null && field.dato !== '' ? field.dato : '«' + (field.label || field.tag || 'valor') + '»'
                  const full = code + (field.remark_mask || '').replace(/\[[^\]]*\]/g, val)
                  const ds = field.datasource || []
                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: hidden ? 'rgba(148,163,184,.12)' : '#f6f9f8',
                        borderRadius: 4,
                        fontSize: 12.5,
                        opacity: hidden ? 0.6 : 1,
                      }}
                    >
                      <code
                        style={{
                          background: '#0f172a',
                          color: '#22d989',
                          padding: '2px 6px',
                          borderRadius: 3,
                          fontSize: 11,
                          flexShrink: 0,
                        }}
                      >
                        {field.campoForm || `field_${idx}`}
                      </code>
                      <div style={{ flex: 1 }}>
                        <strong>{field.label || '(sin label)'}</strong>
                        {field.mandatory ? <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span> : null}
                        {hidden ? (
                          <span
                            className="bia-pill mini"
                            style={{ marginLeft: 6, background: 'rgba(148,163,184,.25)' }}
                            title="Línea automática: el script la arma sola y va en un grupo oculto; el vendedor no la ve"
                          >
                            🔒 auto - oculta
                          </span>
                        ) : null}
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                          <span className="bia-pill mini">{field.typedata || 'text'}</span>{' '}
                          {field.typeremark && field.typeremark !== 'General' ? (
                            <span className="bia-pill mini">{field.typeremark}</span>
                          ) : null}{' '}
                          {csFieldNoWrite(field) ? (
                            <span
                              className="bia-pill mini"
                              title="Dato interno o elemento visual: el runtime NO emite ningún comando por este campo"
                              style={{ opacity: 0.75 }}
                            >
                              no escribe
                            </span>
                          ) : full ? (
                            <>
                              - escribe: <code>{full}</code>
                            </>
                          ) : null}{' '}
                          {field.data_test ? (
                            <>
                              - Ej: <code>{String(field.data_test)}</code>
                            </>
                          ) : null}{' '}
                          {ds.length ? <>- {ds.length} opciones</> : null}
                        </div>
                        {ds.length ? (
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>
                            {ds.slice(0, 5).map((o, i) => (
                              <code
                                key={i}
                                style={{ background: 'rgba(0,0,0,.05)', padding: '1px 4px', borderRadius: 2, marginRight: 3 }}
                              >
                                {o.text || o.value || ''}
                              </code>
                            ))}
                            {ds.length > 5 ? <small> ...y {ds.length - 5} más</small> : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ))
      )}

      {post.length ? (
        <>
          <h4 style={{ margin: '14px 0 8px', color: 'var(--brand-dark)', fontSize: 14 }}>⚡ postCommands ({post.length})</h4>
          <ol
            style={{
              margin: 0,
              padding: '10px 14px 10px 32px',
              background: '#0f172a',
              borderRadius: 6,
              color: '#e2e8f0',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              fontSize: 12,
            }}
          >
            {post.map((c, i) => (
              <li key={i}>
                <code style={{ color: '#22d989' }}>{csCmdLabel(c)}</code>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </>
  )
}

/* Port de renderFormPreview: preview visual de los forms tal como apareceran
   en el Custom Script final. */
function FormPreview({ cs }) {
  const hasFields = (cs.forms || []).some((f) => f.remarks && f.remarks.length > 0)
  const hasCmds = cs.postCommands && cs.postCommands.length > 0
  if (!hasFields && !hasCmds) {
    return <div className="bia-empty">Agrega campos al form (o postCommands) para ver el preview.</div>
  }
  return <ScriptFieldsPreview r={cs} />
}

/* ===========================================================================
   Port de showPreviewModal: arma el single-file (previewMode) y lo muestra en
   un iframe dentro de un CModal. Botones: descargar .html de produccion,
   abrir en pestaña nueva, cerrar. */
function PreviewModal({ data, onClose }) {
  const { dataJson, configJson, realPreview = false } = data
  const [html, setHtml] = useState(null)
  const [error, setError] = useState(null)
  const title = (dataJson && dataJson.title) || state.cs.title || 'Custom Script'

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        // Forzar refresh del cache del runtime para que el preview SIEMPRE use la última app.js de disco
        await csLoadRuntimeTemplates(true)
        const singleHtml = await csBuildSingleFile(dataJson, configJson, {
          previewMode: !realPreview,
          simulateApi: !realPreview,
          realPreview,
          token: state.sessionToken,
          env: state.cs.targetEnv || 'stg',
          title,
        })
        if (alive) setHtml(singleHtml)
      } catch (e) {
        console.error('[boston] error en preview:', e)
        if (alive) setError(e.message || String(e))
      }
    })()
    return () => {
      alive = false
    }
    // eslint-disable-next-line
  }, [])

  function downloadProdHtml() {
    // Generamos un .html SIN previewMode para el archivo descargable (es el de producción)
    csBuildSingleFile(dataJson, configJson, { previewMode: false, title }).then((prodHtml) => {
      const slug =
        (title + '')
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '_')
          .slice(0, 40) || 'custom_script'
      downloadBlob(new Blob([prodHtml], { type: 'text/html' }), `${slug}.html`)
    })
  }

  function openNewTab() {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  const lightBtn = { background: 'rgba(255,255,255,.15)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }

  return (
    <CModal visible size="xl" alignment="center" onClose={onClose}>
      <CModalHeader
        closeButton={false}
        style={{ background: 'linear-gradient(135deg,#0f766e,#0e7490)', color: '#fff', alignItems: 'center', gap: 12 }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: '.5px', textTransform: 'uppercase' }}>
            Vista previa funcional - Single-file
          </div>
          <CModalTitle style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{title}</CModalTitle>
        </div>
        <CButton size="sm" style={lightBtn} title="Descargar .html autocontenido" onClick={downloadProdHtml}>
          <CIcon icon={icon('cilCloudDownload')} /> Descargar HTML único
        </CButton>
        <CButton size="sm" style={lightBtn} title="Abrir en pestaña nueva" onClick={openNewTab}>
          <CIcon icon={icon('cilExternalLink')} /> Pestaña nueva
        </CButton>
        <CButton size="sm" color="danger" title="Cerrar" onClick={onClose}>
          <CIcon icon={icon('cilX')} /> Cerrar
        </CButton>
      </CModalHeader>
      <CModalBody style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            background: '#fef3c7',
            color: '#92400e',
            padding: '8px 20px',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            borderBottom: '1px solid #fcd34d',
          }}
        >
          <span>⚠</span>
          <span>
            <b>{realPreview ? 'Vista previa real:' : 'Vista previa segura:'}</b>{' '}
            {realPreview
              ? 'usa el token Sabre y puede ejecutar comandos reales.'
              : 'las llamadas a Sabre estan simuladas. El form, validacion, postCommands y placeholders se ejecutan reales.'}
          </span>
        </div>
        <div style={{ position: 'relative', background: '#f8fafc', height: 'min(62vh, 720px)' }}>
          {!html && !error ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                font: '500 14px/1.5 system-ui',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                <div>Empaquetando script en un único archivo…</div>
              </div>
            </div>
          ) : null}
          {error ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#dc2626',
                font: '500 14px/1.5 system-ui',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⚠</div>
                <div>
                  <b>Error armando el preview</b>
                </div>
                <div style={{ fontSize: 12, marginTop: 6, opacity: 0.85 }}>{error}</div>
                <div style={{ fontSize: 11, marginTop: 8, opacity: 0.6 }}>Revisá la consola para detalles.</div>
              </div>
            </div>
          ) : null}
          {html ? (
            <iframe
              title="boston-preview"
              sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
              srcDoc={html}
              style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
            />
          ) : null}
        </div>
        <div
          style={{
            padding: '8px 20px',
            background: '#f1f5f9',
            borderTop: '1px solid #e2e8f0',
            font: '12px/1.4 system-ui',
            color: '#475569',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            <b>{(dataJson && dataJson.forms && dataJson.forms.length) || 0}</b> form(s) -{' '}
            <b>{(dataJson && dataJson.postCommands && dataJson.postCommands.length) || 0}</b> postCommand(s)
          </span>
          <span style={{ opacity: 0.7 }}>
            UUID: <code>{(configJson && configJson.config && configJson.config.uuid) || '—'}</code>
          </span>
        </div>
      </CModalBody>
    </CModal>
  )
}

/* Editor del datasource de un field tipo select. Mantiene el texto local mientras
   se tipea (igual que el original: el JSON invalido no pisa el estado) y solo
   persiste en state cuando parsea OK. */
function DatasourceEditor({ field }) {
  const [text, setText] = useState(() => JSON.stringify(field.datasource || [], null, 2))
  useEffect(() => {
    setText(JSON.stringify(field.datasource || [], null, 2))
    // eslint-disable-next-line
  }, [field])
  return (
    <div style={{ marginTop: 10 }}>
      <CFormLabel>{'Datasource (JSON [{value,text}])'}</CFormLabel>
      <CFormTextarea
        className="bia-mono"
        rows={3}
        value={text}
        onChange={(event) => {
          setText(event.target.value)
          try {
            field.datasource = JSON.parse(event.target.value || '[]')
            emit()
          } catch {}
        }}
      />
    </div>
  )
}

const GRID3 = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }

function parseConfig(value) {
  if (!value) return {}
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return {}
  }
}

function shortCommit(value) {
  return String(value || '').slice(0, 12)
}

function CustomScriptView() {
  const state = useBostonState()
  const { toast, confirm } = useUI()
  const mockModeEnabled = new URLSearchParams(window.location.search).get('mock') === '1'
  const [recipesOpen, setRecipesOpen] = useState(false)
  const [recipesFilter, setRecipesFilter] = useState('')
  const [preview, setPreview] = useState(null) // { dataJson, configJson }
  const [packageValidation, setPackageValidation] = useState(null)
  const [draftSave, setDraftSave] = useState(null)
  const [publishTest, setPublishTest] = useState(null)
  const [publishReleaseTest, setPublishReleaseTest] = useState(null)
  const [publishRelease, setPublishRelease] = useState(null)

  const cs = state.cs
  if (!cs.uuid) cs.uuid = uuidV4()
  const isPersistedCustomScript = cs.persisted === true || cs.isSaved === true
  const publishReleaseDisabled = publishRelease?.loading || !isPersistedCustomScript
  const csConfig = parseConfig(cs.config)
  const publishState = csConfig.publish || csConfig.publication || {}
  const publishedUrl = cs.url || publishState.url || ''
  const isPublished = Boolean(publishedUrl || publishState.published === true)
  const folderPath = (state.config.baseFolder || 'admin_v2_local_library\\').replace(/[\\/]?$/, '\\') + cs.name + '\\'
  const dataJsonPreview = JSON.stringify(csBuildDataJson(), null, 2)

  /* ===== handlers (port de bindCustom) ===== */
  function openFunctionalPreview(opts = {}) {
    if (opts.realPreview !== true && !mockModeEnabled) return
    if (!state.cs.forms.length && !state.cs.postCommands.length) {
      toast('El script no tiene forms ni postCommands.', 'warning')
      return
    }
    if (opts.realPreview && !state.sessionToken) {
      toast('Falta token Sabre para ejecutar comandos reales.', 'warning')
      return
    }
    setPreview({ dataJson: csBuildDataJson(), configJson: csBuildConfigJson(), realPreview: opts.realPreview === true })
  }

  async function regenUuid() {
    const ok = await confirm({
      title: 'Regenerar UUID?',
      body: 'Romperia la asociacion en Boston si ya estaba publicado.',
      confirmText: 'Regenerar',
      tone: 'danger',
    })
    if (!ok) return
    state.cs.uuid = uuidV4()
    state.cs.persisted = false
    state.cs.isSaved = false
    state.cs.url = ''
    state.cs.config = {}
    emit()
  }

  async function resetBuilder() {
    const ok = await confirm({
      title: '¿Vaciar el formulario y empezar un script nuevo?',
      body: 'Se pierde lo que no hayas guardado.',
      confirmText: 'Vaciar',
      tone: 'danger',
    })
    if (!ok) return
    state.cs = {
      name: 'mi_script',
      title: 'MI_SCRIPT',
      description: '',
      tags: '',
      uuid: uuidV4(),
      validateActiveReservation: true,
      sendCommands: false,
      orderExecute: true,
      editCatalogues: false,
      reintentos: 0,
      targetEnv: state.cs.targetEnv || 'stg',
      forms: [{ form: 'form1', title: 'FORMULARIO', title_visible: true, remarks: [] }],
      postCommands: [],
      persisted: false,
      isSaved: false,
      url: '',
      config: {},
    }
    emit()
  }

  function previewRecipe(id) {
    if (!mockModeEnabled) return
    const p = csBuildRecipePreview(id)
    if (p) setPreview(p)
  }

  function setMeta(key, value) {
    state.cs[key] = value
    emit()
  }

  function setForm(fIdx, key, value) {
    state.cs.forms[fIdx][key] = value
    emit()
  }

  function setField(fIdx, idx, key, value) {
    state.cs.forms[fIdx].remarks[idx][key] = value
    emit()
  }

  function addField(fIdx) {
    state.cs.forms[fIdx].remarks.push(newField())
    emit()
  }

  function delField(fIdx, idx) {
    state.cs.forms[fIdx].remarks.splice(idx, 1)
    emit()
  }

  function delForm(fIdx) {
    if (state.cs.forms.length > 1) {
      state.cs.forms.splice(fIdx, 1)
      emit()
    }
  }

  function addForm() {
    const n = state.cs.forms.length + 1
    state.cs.forms.push({ form: `form${n}`, title: `FORMULARIO ${n}`, title_visible: true, remarks: [] })
    emit()
  }

  function copyDataJson() {
    if (navigator.clipboard) navigator.clipboard.writeText(JSON.stringify(csBuildDataJson(), null, 2))
  }

  async function downloadZip() {
    try {
      const blob = await csBuildZip()
      downloadBlob(blob, `${state.cs.name}.zip`)
    } catch (e) {
      toast(`Error armando ZIP: ${e.message}`, 'danger')
    }
  }

  async function downloadSingleFile() {
    try {
      if (!state.cs.forms.length && !state.cs.postCommands.length) {
        toast('El script no tiene forms ni postCommands.', 'warning')
        return
      }
      const html = await csBuildSingleFile(csBuildDataJson(), csBuildConfigJson(), { previewMode: false })
      downloadBlob(new Blob([html], { type: 'text/html' }), `${state.cs.name}.html`)
    } catch (e) {
      toast(`Error armando HTML único: ${e.message}`, 'danger')
    }
  }

  async function validatePackage() {
    setPackageValidation({ loading: true })
    try {
      const payload = await csBuildPublishPayload()
      const response = await biaValidateCustomScriptPublish(payload)
      setPackageValidation({ loading: false, data: response })
      toast('Paquete validado correctamente.', 'success')
    } catch (e) {
      setPackageValidation({ loading: false, error: e.message || String(e) })
      toast(`Error validando paquete: ${e.message || String(e)}`, 'danger')
    }
  }

  async function simulatePublish() {
    if (!mockModeEnabled) return
    setPublishTest({ loading: true })
    try {
      const payload = await csBuildPublishPayload()
      const response = await biaPublishTestCustomScript(payload)
      setPublishTest({ loading: false, data: response })
      toast('Publicacion simulada correctamente.', 'success')
    } catch (e) {
      setPublishTest({ loading: false, error: e.message || String(e) })
      toast(`Error simulando publicacion: ${e.message || String(e)}`, 'danger')
    }
  }

  async function simulateRelease(targetEnv) {
    if (!mockModeEnabled) return
    setPublishReleaseTest({ loading: true, targetEnv })
    try {
      const payload = await csBuildPublishPayload()
      const response = await biaPublishReleaseTestCustomScript({
        ...payload,
        targetEnv,
        script: {
          ...payload.script,
          targetEnv,
        },
      })
      setPublishReleaseTest({ loading: false, data: response })
      toast(`Release ${targetEnv.toUpperCase()} simulado correctamente.`, 'success')
    } catch (e) {
      setPublishReleaseTest({ loading: false, targetEnv, error: e.message || String(e) })
      toast(`Error simulando release ${targetEnv.toUpperCase()}: ${e.message || String(e)}`, 'danger')
    }
  }

  function buildDraftPayload() {
    const logicJson = state.cs.logicJson || { postCommands: state.cs.postCommands || [] }
    return {
      script: {
        uuid: state.cs.uuid,
        name: state.cs.name,
        description: state.cs.description || '',
        url: '',
        status: 0,
      },
      config: {
        dataJson: csBuildDataJson(),
        configJson: csBuildConfigJson(),
        logicJson,
        handlerJs: state.cs.handlerJs || null,
        fichaMd: state.cs.fichaMd || null,
      },
      updatedUser: 'admin_v2',
    }
  }

  async function saveDraft() {
    setDraftSave({ loading: true })
    try {
      const response = await biaSaveDraftCustomScript(buildDraftPayload())
      if (response?.ok === false) {
        throw new Error(response.message || 'No se pudo guardar el script.')
      }

      state.cs.persisted = true
      state.cs.isSaved = true
      state.cs.config = {
        ...parseConfig(state.cs.config),
        ...parseConfig(response?.config),
      }
      const libraryItem = (state.library || []).find((item) => item.uuid === state.cs.uuid)
      if (libraryItem) {
        libraryItem.persisted = true
        libraryItem.isSaved = true
        libraryItem.config = state.cs.config
        Storage.saveLibrary(state.library)
      }
      emit()

      setDraftSave({ loading: false, data: response })
      toast(response?.created ? 'Script guardado en custom_scripts.' : 'Script actualizado en custom_scripts.', 'success')
    } catch (e) {
      setDraftSave({ loading: false, error: e.message || String(e) })
      toast(`Error guardando script: ${e.message || String(e)}`, 'danger')
    }
  }

  async function publishReleaseToGitHub(targetEnv) {
    if (!isPersistedCustomScript) {
      const message = 'Guarda el script antes de publicar.'
      setPublishRelease({ loading: false, targetEnv, error: message })
      toast(message, 'warning')
      return
    }

    const ok = await confirm({
      title: `Publicar ${targetEnv.toUpperCase()}?`,
      body: (
        <span style={{ whiteSpace: 'pre-line' }}>
          {`Esto creara un commit y un release/tag en basset-la/customScriptsUsuarios.\n\nAmbiente: ${targetEnv}\nUUID: ${state.cs.uuid}\n\nNo se usara PHP ni se llamara CircleCI manualmente.`}
        </span>
      ),
      confirmText: `Publicar ${targetEnv.toUpperCase()}`,
      cancelText: 'Cancelar',
      tone: 'warning',
    })
    if (!ok) return

    setPublishRelease({ loading: true, targetEnv })
    try {
      const payload = await csBuildPublishPayload()
      const response = await biaPublishReleaseCustomScript({
        ...payload,
        targetEnv,
        script: {
          ...payload.script,
          targetEnv,
        },
      })
      if (response?.ok === false) {
        throw new Error(response.message || 'No se pudo publicar el Custom Script.')
      }
      const publish = {
        published: true,
        targetEnv: response.targetEnv || targetEnv,
        url: response.url,
        releaseTag: response.releaseTag,
        commitSha: response.commitSha,
        releaseUrl: response.releaseUrl,
        publishedAt: response.publishedAt || new Date().toISOString(),
        publishedBy: response.publishedBy || 'admin_v2',
      }
      state.cs.url = response.url || state.cs.url || ''
      state.cs.config = {
        ...parseConfig(state.cs.config),
        publish,
      }
      state.cs.persisted = true
      state.cs.isSaved = true
      const libraryItem = (state.library || []).find((item) => item.uuid === state.cs.uuid)
      if (libraryItem) {
        libraryItem.url = state.cs.url
        libraryItem.config = state.cs.config
        libraryItem.persisted = true
        libraryItem.isSaved = true
        Storage.saveLibrary(state.library)
      }
      emit()
      setPublishRelease({ loading: false, data: response })
      toast(`Custom Script publicado en ${targetEnv.toUpperCase()}.`, 'success')
    } catch (e) {
      setPublishRelease({ loading: false, targetEnv, error: e.message || String(e) })
      toast(`Error publicando ${targetEnv.toUpperCase()}: ${e.message || String(e)}`, 'danger')
    }
  }

  async function saveAndPublish() {
    try {
      const { item, result } = await csPersistAndPublish()
      try {
        await csProposeToKB(item)
      } catch (e) {}
      toast(
        <span style={{ whiteSpace: 'pre-line' }}>
          {`UUID: ${item.uuid}\nArchivos: ${result.files.join(', ')}\n\nRuta local:\n${result.path}\n\nPublicacion remota pendiente para fase posterior.`}
        </span>,
        'success',
        { title: 'Custom Script guardado localmente.', delay: 12000 },
      )
    } catch (e) {
      const ok = await confirm({
        title: 'No se pudo guardar localmente',
        body: (
          <span style={{ whiteSpace: 'pre-line' }}>{`No se pudo guardar localmente (${e.message}).\n\nDescargar ZIP manualmente?`}</span>
        ),
        confirmText: 'Descargar ZIP',
        tone: 'warning',
      })
      if (ok) {
        try {
          const blob = await csBuildZip()
          downloadBlob(blob, `${state.cs.name}.zip`)
        } catch (err) {
          toast(`Tampoco ZIP: ${err.message}`, 'danger')
        }
      }
    }
    emit() // renderSideFoot() en el original: refresca contadores de la biblioteca
  }

  /* ===== recetas: agrupado por categoria + filtro (port del filtro DOM) ===== */
  const byCat = {}
  for (const r of RECIPES) {
    const c = r.category || 'Otras'
    ;(byCat[c] = byCat[c] || []).push(r)
  }
  const order = ['Consulta', 'Remarks', 'Vuelos', 'Hoteles', 'Autos', 'Pasajeros', 'Ticketing', 'Queues', 'Operacionales', 'Otras']
  const cats = Object.keys(byCat).sort((a, b) => order.indexOf(a) - order.indexOf(b))
  const q = recipesFilter.toLowerCase().trim()
  const recipeMatches = (r) => !q || (r.title + ' ' + r.description).toLowerCase().includes(q)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Custom Script Builder</h1>
          <p>
            Genera localmente el paquete de archivos del Custom Script. Cada script mantiene su UUID; la publicacion remota
            queda pendiente para fase posterior.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <CButton color="secondary" variant="ghost" title="Vacía el formulario para empezar un script nuevo desde cero" onClick={resetBuilder}>
            <CIcon icon={icon('cilBrushAlt')} /> Nuevo / Limpiar
          </CButton>
          {mockModeEnabled ? (
            <CButton color="secondary" variant="ghost" onClick={() => openFunctionalPreview()}>
              <CIcon icon={icon('cilSearch')} /> Vista previa segura
            </CButton>
          ) : null}
          <CButton color="success" variant="outline" onClick={() => openFunctionalPreview({ realPreview: true })}>
            <CIcon icon={icon('cilMediaPlay')} /> Vista previa real con token
          </CButton>
          <CButton color="secondary" variant="ghost" onClick={() => setRecipesOpen((v) => !v)}>
            <CIcon icon={icon('cilBook')} /> Recetas
          </CButton>
          <CButton color="secondary" variant="ghost" onClick={regenUuid}>
            <CIcon icon={icon('cilLoopCircular')} /> Regenerar UUID
          </CButton>
          <CButton color="secondary" variant="ghost" onClick={downloadZip}>
            <CIcon icon={icon('cilCloudDownload')} /> Descargar .zip
          </CButton>
          <CButton color="secondary" variant="ghost" title="Empaqueta todo en UN solo .html autocontenido" onClick={downloadSingleFile}>
            <CIcon icon={icon('cilFile')} /> Descargar .html único
          </CButton>
          <CButton color="info" variant="outline" disabled={packageValidation?.loading} onClick={validatePackage}>
            {packageValidation?.loading ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCheckCircle')} />} Validar paquete
          </CButton>
          {mockModeEnabled ? (
            <>
              <CButton color="warning" variant="outline" disabled={publishTest?.loading} onClick={simulatePublish}>
                {publishTest?.loading ? <CSpinner size="sm" /> : <CIcon icon={icon('cilExternalLink')} />} Simular publicacion
              </CButton>
              <CButton color="warning" variant="outline" disabled={publishReleaseTest?.loading} onClick={() => simulateRelease('stg')}>
                {publishReleaseTest?.loading && publishReleaseTest?.targetEnv === 'stg' ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCloudUpload')} />} Simular release STG
              </CButton>
              <CButton color="warning" variant="outline" disabled={publishReleaseTest?.loading} onClick={() => simulateRelease('prod')}>
                {publishReleaseTest?.loading && publishReleaseTest?.targetEnv === 'prod' ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCloudUpload')} />} Simular release PROD
              </CButton>
            </>
          ) : null}
          <CButton color="primary" variant="outline" disabled={draftSave?.loading} onClick={saveDraft}>
            {draftSave?.loading ? <CSpinner size="sm" /> : <CIcon icon={icon('cilSave')} />} Guardar script
          </CButton>
          <CButton
            color="danger"
            variant="outline"
            disabled={publishReleaseDisabled}
            title={!isPersistedCustomScript ? 'Guarda el script antes de publicar.' : undefined}
            onClick={() => publishReleaseToGitHub('stg')}
          >
            {publishRelease?.loading && publishRelease?.targetEnv === 'stg' ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCloudUpload')} />} Publicar STG
          </CButton>
          <CButton
            color="danger"
            variant="outline"
            disabled={publishReleaseDisabled}
            title={!isPersistedCustomScript ? 'Guarda el script antes de publicar.' : undefined}
            onClick={() => publishReleaseToGitHub('prod')}
          >
            {publishRelease?.loading && publishRelease?.targetEnv === 'prod' ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCloudUpload')} />} Publicar PROD
          </CButton>
          <CButton color="success" className="btn-brand" onClick={saveAndPublish}>
            <CIcon icon={icon('cilSave')} /> Guardar local
          </CButton>
        </div>
      </div>

      {!isPersistedCustomScript ? (
        <CAlert color="warning">Guarda el script antes de publicar.</CAlert>
      ) : null}

      {draftSave && !draftSave.loading ? (
        <CAlert color={draftSave.error ? 'danger' : 'success'}>
          {draftSave.error ? (
            <span>{draftSave.error}</span>
          ) : (
            <span>
              Script guardado: <code>{draftSave.data?.scriptUuid}</code> -{' '}
              {draftSave.data?.created ? 'registro creado' : 'registro actualizado'}
            </span>
          )}
        </CAlert>
      ) : null}

      <div className="card-x">
        <h3>Estado de publicacion</h3>
        {!isPublished ? (
          <p className="text-muted" style={{ marginBottom: 0 }}>
            Pendiente de publicacion.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            <div>
              Publicado en <strong>{String(publishState.targetEnv || state.cs.targetEnv || 'stg').toUpperCase()}</strong>
            </div>
            {publishedUrl ? (
              <div>
                URL publica:{' '}
                <a href={publishedUrl} target="_blank" rel="noreferrer">
                  {publishedUrl}
                </a>
              </div>
            ) : null}
            {publishState.releaseTag ? (
              <div>
                Release tag: <code>{publishState.releaseTag}</code>
              </div>
            ) : null}
            {publishState.commitSha ? (
              <div>
                Commit: <code>{shortCommit(publishState.commitSha)}</code>
              </div>
            ) : null}
            {publishState.releaseUrl ? (
              <div>
                Release:{' '}
                <a href={publishState.releaseUrl} target="_blank" rel="noreferrer">
                  abrir en GitHub
                </a>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {packageValidation && !packageValidation.loading ? (
        <CAlert color={packageValidation.error ? 'danger' : 'success'}>
          {packageValidation.error ? (
            <span>{packageValidation.error}</span>
          ) : (
            <span>
              Archivos recibidos: <strong>{packageValidation.data?.filesReceived}</strong> - Tamaño total:{' '}
              <strong>{packageValidation.data?.totalSizeBytes}</strong> bytes - Listo para publicar:{' '}
              <strong>{packageValidation.data?.readyToPublish ? 'si' : 'no'}</strong>
            </span>
          )}
        </CAlert>
      ) : null}

      {publishTest && !publishTest.loading ? (
        <CAlert color={publishTest.error ? 'danger' : 'info'}>
          {publishTest.error ? (
            <span>{publishTest.error}</span>
          ) : (
            <span>
              URL: <code>{publishTest.data?.url}</code>
              <br />
              Base URL: <code>{publishTest.data?.baseUrl}</code>
              <br />
              Archivos recibidos: <strong>{publishTest.data?.filesReceived}</strong> - Tamaño total:{' '}
              <strong>{publishTest.data?.totalSizeBytes}</strong> bytes - Test mode:{' '}
              <strong>{publishTest.data?.testMode ? 'si' : 'no'}</strong>
            </span>
          )}
        </CAlert>
      ) : null}

      {publishReleaseTest && !publishReleaseTest.loading ? (
        <CAlert color={publishReleaseTest.error ? 'danger' : 'info'}>
          {publishReleaseTest.error ? (
            <span>{publishReleaseTest.error}</span>
          ) : (
            <span>
              Target env: <strong>{publishReleaseTest.data?.targetEnv}</strong>
              <br />
              Repo: <code>{publishReleaseTest.data?.repo}</code>
              <br />
              Base branch: <code>{publishReleaseTest.data?.baseBranch}</code>
              <br />
              Repo base path: <code>{publishReleaseTest.data?.repoBasePath}</code>
              <br />
              URL: <code>{publishReleaseTest.data?.url}</code>
              <br />
              Release tag: <code>{publishReleaseTest.data?.releaseTag}</code>
              <br />
              Versioning mode: <strong>{publishReleaseTest.data?.versioningMode}</strong> - Replace mode:{' '}
              <strong>{publishReleaseTest.data?.replaceMode}</strong>
              <br />
              Files to write:
              <pre className="bia-pre" style={{ marginTop: 8, maxHeight: 180, overflow: 'auto' }}>
                {(publishReleaseTest.data?.filesToWrite || []).join('\n')}
              </pre>
            </span>
          )}
        </CAlert>
      ) : null}

      {publishRelease && !publishRelease.loading ? (
        <CAlert color={publishRelease.error ? 'danger' : 'success'}>
          {publishRelease.error ? (
            <span>{publishRelease.error}</span>
          ) : (
            <span>
              URL: <code>{publishRelease.data?.url}</code>
              <br />
              Release tag: <code>{publishRelease.data?.releaseTag}</code>
              <br />
              Commit SHA: <code>{publishRelease.data?.commitSha}</code>
              <br />
              Release URL: <code>{publishRelease.data?.releaseUrl}</code>
              <br />
              Files written:
              <pre className="bia-pre" style={{ marginTop: 8, maxHeight: 180, overflow: 'auto' }}>
                {(publishRelease.data?.filesWritten || []).join('\n')}
              </pre>
            </span>
          )}
        </CAlert>
      ) : null}

      {recipesOpen && (
        <div className="card-x">
          <div className="bia-card-head">
            <h3>
              📚 Catalogo de recetas profesionales <span className="bia-pill">{RECIPES.length}</span>
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <CFormInput
                size="sm"
                placeholder="🔎 Buscar receta..."
                style={{ minWidth: 240 }}
                value={recipesFilter}
                onChange={(event) => setRecipesFilter(event.target.value)}
              />
              <CButton size="sm" color="secondary" variant="ghost" onClick={() => setRecipesOpen(false)}>
                <CIcon icon={icon('cilX')} /> Cerrar
              </CButton>
            </div>
          </div>
          {cats.map((cat) => {
            const visible = byCat[cat].filter(recipeMatches)
            if (!visible.length) return null
            return (
              <div key={cat}>
                <h4
                  style={{
                    margin: '18px 0 8px',
                    color: 'var(--brand-dark)',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ background: 'var(--brand-dark)', color: 'white', padding: '2px 10px', borderRadius: 12, fontSize: 11 }}>
                    {cat}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 400 }}>{byCat[cat].length} recetas</span>
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                  {visible.map((r) => (
                    <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#fff' }}>
                      <div style={{ fontWeight: 600 }}>{r.title}</div>
                      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 10px' }}>{r.description}</p>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {mockModeEnabled ? (
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="ghost"
                            title="Ver como queda el form sin aplicar al builder"
                            onClick={() => previewRecipe(r.id)}
                          >
                            <CIcon icon={icon('cilSearch')} /> Vista previa
                          </CButton>
                        ) : null}
                        <CButton size="sm" color="success" className="btn-brand" onClick={() => csApplyRecipe(r.id)}>
                          <CIcon icon={icon('cilCheck')} /> Usar receta
                        </CButton>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="card-x">
        <h3>Metadata</h3>
        <div style={GRID3}>
          <div>
            <CFormLabel>Nombre carpeta</CFormLabel>
            <CFormInput
              className="bia-mono"
              value={cs.name}
              onChange={(event) => setMeta('name', event.target.value.replace(/[^a-z0-9-_]+/gi, '_') || 'mi_script')}
            />
          </div>
          <div>
            <CFormLabel>Title</CFormLabel>
            <CFormInput value={cs.title} onChange={(event) => setMeta('title', event.target.value)} />
          </div>
          <div>
            <CFormLabel>UUID</CFormLabel>
            <CFormInput className="bia-mono" value={cs.uuid} readOnly />
          </div>
          <div>
            <CFormLabel>Target env</CFormLabel>
            <CFormSelect value={cs.targetEnv} onChange={(event) => setMeta('targetEnv', event.target.value)}>
              <option value="stg">STG</option>
              <option value="prod">PROD</option>
            </CFormSelect>
          </div>
          <div>
            <CFormLabel>Tags</CFormLabel>
            <CFormInput value={cs.tags} placeholder="qc, remarks" onChange={(event) => setMeta('tags', event.target.value)} />
          </div>
          <div>
            <CFormLabel>Reintentos</CFormLabel>
            <CFormInput
              type="number"
              value={cs.reintentos}
              onChange={(event) => setMeta('reintentos', Number(event.target.value) || 0)}
            />
          </div>
        </div>
        <CAlert color="info" className="mt-3 mb-2">
          <strong>Ruta destino:</strong> <code>{folderPath}</code>
          <br />
          <strong>Publicacion remota:</strong>{' '}
          <code>Publicacion remota pendiente para fase posterior</code>
        </CAlert>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
          <CFormCheck
            id="cs-validate"
            label="Requiere reserva activa"
            checked={!!cs.validateActiveReservation}
            onChange={(event) => setMeta('validateActiveReservation', event.target.checked)}
          />
          <CFormCheck
            id="cs-sendcmd"
            label="sendCommands"
            checked={!!cs.sendCommands}
            onChange={(event) => setMeta('sendCommands', event.target.checked)}
          />
          <CFormCheck
            id="cs-order"
            label="orderExecute"
            checked={!!cs.orderExecute}
            onChange={(event) => setMeta('orderExecute', event.target.checked)}
          />
          <CFormCheck
            id="cs-catalog"
            label="editCatalogues"
            checked={!!cs.editCatalogues}
            onChange={(event) => setMeta('editCatalogues', event.target.checked)}
          />
        </div>
      </div>

      {cs.forms.map((form, fIdx) => (
        <div className="card-x" key={fIdx}>
          <div className="bia-card-head">
            <h3>Form: {form.title}</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <CButton size="sm" color="secondary" variant="outline" onClick={() => addField(fIdx)}>
                + Campo
              </CButton>
              {cs.forms.length > 1 && (
                <CButton size="sm" color="danger" onClick={() => delForm(fIdx)}>
                  <CIcon icon={icon('cilTrash')} /> Form
                </CButton>
              )}
            </div>
          </div>
          <div className="bia-grid-2" style={{ marginTop: 10 }}>
            <div>
              <CFormLabel>Form ID</CFormLabel>
              <CFormInput className="bia-mono" value={form.form} onChange={(event) => setForm(fIdx, 'form', event.target.value)} />
            </div>
            <div>
              <CFormLabel>Titulo del form</CFormLabel>
              <CFormInput value={form.title} onChange={(event) => setForm(fIdx, 'title', event.target.value)} />
            </div>
          </div>
          {form.remarks.length === 0 ? (
            <div className="bia-empty" style={{ marginTop: 12 }}>
              Sin campos. Click "+ Campo".
            </div>
          ) : (
            form.remarks.map((field, idx) => (
              <div
                key={idx}
                style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginTop: 12, background: '#fff' }}
              >
                <div style={GRID3}>
                  <div>
                    <CFormLabel>Label</CFormLabel>
                    <CFormInput value={field.label} onChange={(event) => setField(fIdx, idx, 'label', event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Tipo</CFormLabel>
                    <CFormSelect value={field.typedata} onChange={(event) => setField(fIdx, idx, 'typedata', event.target.value)}>
                      {FIELD_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </CFormSelect>
                  </div>
                  <div>
                    <CFormLabel>Tipo remark</CFormLabel>
                    <CFormSelect value={field.typeremark} onChange={(event) => setField(fIdx, idx, 'typeremark', event.target.value)}>
                      {REMARK_TYPES.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </CFormSelect>
                  </div>
                  <div>
                    <CFormLabel>Remark mask</CFormLabel>
                    <CFormInput
                      className="bia-mono"
                      value={field.remark_mask}
                      placeholder="CC- - APR- - 5H-"
                      onChange={(event) => setField(fIdx, idx, 'remark_mask', event.target.value)}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 18 }}>
                    <CFormCheck
                      id={`cs-mand-${fIdx}-${idx}`}
                      label="Mandatorio"
                      checked={!!field.mandatory}
                      onChange={(event) => setField(fIdx, idx, 'mandatory', event.target.checked)}
                    />
                    <CButton size="sm" color="danger" style={{ marginLeft: 'auto' }} onClick={() => delField(fIdx, idx)}>
                      <CIcon icon={icon('cilTrash')} />
                    </CButton>
                  </div>
                </div>
                {field.typedata === 'select' && <DatasourceEditor field={field} />}
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                  tag: <code>{field.tag || `field_${idx}`}</code>
                </div>
              </div>
            ))
          )}
        </div>
      ))}

      <div className="card-x" style={{ textAlign: 'center' }}>
        <CButton color="secondary" variant="ghost" onClick={addForm}>
          + Agregar otro form
        </CButton>
      </div>

      <div className="card-x">
        <div className="bia-card-head">
          <h3>👁 Preview del form (como lo vera el agente en Sabre Web)</h3>
          <span className="bia-pill">Live preview</span>
        </div>
        <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 12, marginTop: 10 }}>
          <FormPreview cs={cs} />
        </div>
      </div>

      <div className="card-x">
        <div className="bia-card-head">
          <h3>Preview data.json</h3>
          <CButton size="sm" color="secondary" variant="ghost" onClick={copyDataJson}>
            <CIcon icon={icon('cilCopy')} /> Copiar
          </CButton>
        </div>
        <pre className="bia-pre" style={{ maxHeight: 400, overflowY: 'auto' }}>{dataJsonPreview}</pre>
      </div>

      {preview && <PreviewModal data={preview} onClose={() => setPreview(null)} />}
    </>
  )
}

export default CustomScriptView
